import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import Button from '@amzn/awsui-components-console/button';
import Flashbar, {
  FlashbarProps,
} from '@amzn/awsui-components-console/flashbar';
import Spinner from '@amzn/awsui-components-console/spinner';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useDraftState } from './hooks/useDraftState';
import { useStringGroupData } from './hooks/useStringGroupData';
import { useStringsData } from './hooks/useStringsData';
import {
  ChangeControlRefusal,
  useSubmitTranslation,
} from './hooks/useSubmitTranslation';
import { useVersionHistory } from './hooks/useVersionHistory';
import PageHeader from './components/PageHeader';
import DescriptionPanel from './components/DescriptionPanel';
import TranslationProgressPanel from './components/TranslationProgressPanel';
import MetadataPanel from './components/MetadataPanel';
import StringsTable from './components/StringsTable';
import CreateStringModal from './components/CreateStringModal';
import EditStringModal from './components/EditStringModal';
import EditGroupModal from './components/EditGroupModal';
import SubmitCancelActionBar from './components/SubmitCancelActionBar';
import CancelTranslationModal from './components/CancelTranslationModal';
import SubmitTranslationModal from './components/SubmitTranslationModal';
import ClearDraftModal from './components/ClearDraftModal';
import ChangeControlBlockedModal from './components/ChangeControlBlockedModal';
import VersionHistoryPanel from './components/VersionHistoryPanel';
import DiffModal from './components/DiffModal';
import ViewVersionModal from './components/ViewVersionModal';
import RollbackModal from './components/RollbackModal';
import SubmissionAcknowledgementModal from './components/SubmissionAcknowledgementModal';
import RenameStringGroupModal from './components/RenameStringGroupModal';
import InterimSubmissionBanner from './components/InterimSubmissionBanner';
import {
  DraftState,
  DraftStringInput,
  GroupEdits,
  NewStringGroupMetadata,
  StringInfo,
  TranslationStatus,
} from './types';
import { getClientDisplayName, isLangHubClient } from './utils/clientIdMapper';
import { draftEditsLiveStrings } from './utils/draftEditsLiveStrings';
import { renameDraft } from './utils/draftStorage';
import { flashAriaRole } from './utils/flashAriaRole';
import { getAppSyncErrorType } from 'src/api/graphqlErrorType';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import BindleAccessLinks from 'src/common/authz/BindleAccessLinks';
import { resolveBindleAccessLink } from 'src/common/utils/bindleLinks';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { useNamespacePermissions } from 'src/common/hooks/useNamespacePermissions';
import { useChangeControlStatus } from 'src/common/ui/ChangeControlBanner';
import { recordRumEvent } from 'src/common/rum/rum';
import { RUM_CLICK_EVENT } from 'src/common/constants';
import {
  STRING_GROUP_NOT_READY_HEADER_KEY,
  STRING_GROUP_NOT_READY_MESSAGE_KEY,
  UNSUBMITTED_GROUP_LOSS_HEADER_KEY,
  UNSUBMITTED_GROUP_LOSS_MESSAGE_KEY,
} from './constants';
import styles from './StringGroupDetail.module.scss';
import StackedBreadcrumbGroup from 'src/common/ui/StackedBreadcrumbGroup';
import {
  buildNamespacePath,
  buildStringGroupPath,
  TRACK_MY_TRANSLATIONS_PATH,
} from 'src/common/routes';
import { useTranslation } from 'src/i18n/shim/useTranslation';

const FLASHBAR_AUTO_DISMISS_MS = 5000;

/**
 * Stable id for the "change staged" info flash a group edit raises before
 * chaining into the submit dialog. Fixed (not timestamped) so the effect that
 * clears it when that dialog closes can target it: it is an `info` flash, which
 * addFlashItem never auto-dismisses, so once the submit resolves its own
 * success/error flash this note would otherwise linger beside it and read as a
 * contradiction ("change staged…" next to "submitted successfully").
 */
const GROUP_EDIT_STAGED_FLASH_ID = 'group-edit-staged';

/**
 * How often to re-query the group while a submission is awaiting TRANSCOM
 * confirmation. TRANSCOM confirms the submission once describeStringGroup stops
 * answering StringGroupNotReadyException, so polling resolves the pending screen
 * IN PLACE (no full reload) — and, because the signal is server-side, it works
 * even when the pending screen was reached by reloading or opening a new tab.
 */
const SUBMISSION_POLL_INTERVAL_MS = 10000;

/**
 * How often to re-query the group while TRANSCOM reports the translation
 * IN_PROGRESS. TRANSCOM does not push status changes, so the page only learns a
 * locale finished — or the group reached COMPLETE/FAILED — by re-querying. This
 * poll keeps the Translation Progress panel and status advancing to completion
 * without a manual reload, and stops the moment the status leaves IN_PROGRESS.
 */
const IN_PROGRESS_POLL_INTERVAL_MS = 10000;

/** Shape of the router navigation state this page reads. */
interface StringGroupLocationState {
  newStringGroup?: NewStringGroupMetadata;
}

/**
 * Narrows `useLocation().state` (typed `any`/`unknown` by the router) to the
 * only entry this page consumes, so the cast lives in exactly one place.
 */
function readNewStringGroupState(
  state: unknown,
): NewStringGroupMetadata | undefined {
  return (state as StringGroupLocationState | null)?.newStringGroup;
}

/**
 * Whether the server-fetched strings snapshot already reflects a retained draft.
 *
 * The strings table is fed by ListStrings (TRANSCOM), which ingests a
 * submission INDEPENDENTLY of describeStringGroup's metadata version — the
 * version can advance a beat before TRANSCOM returns the new content. Clearing
 * the draft on the version signal alone therefore blanks the user's just-
 * submitted rows for that gap (the rows the merged table was showing via the
 * draft vanish the moment it is cleared). Gating the clear on this check instead
 * keeps the draft — and, through the table's draft/server merge, the user's
 * rows — on screen until the published content is actually queryable, so the
 * swap to server rows is seamless.
 *
 * Structural on purpose: added stringIds must be PRESENT and deleted ones ABSENT
 * — both reliable because a stringId is never transformed server-side. Edited
 * strings are not text-compared (TRANSCOM may normalise text, which would strand
 * the draft forever); their row stays visible regardless, so a brief post-clear
 * text refresh is acceptable where a permanent phantom draft is not.
 */
function serverStringsReflectDraft(
  strings: StringInfo[],
  draft: DraftState | null,
): boolean {
  if (!draft) return true;
  const serverIds = new Set(strings.map((s) => s.stringId));
  for (const added of draft.added) {
    if (!serverIds.has(added.stringId)) return false;
  }
  for (const deletedId of draft.deleted) {
    if (serverIds.has(deletedId)) return false;
  }
  return true;
}

const StringGroupDetailPage: React.FC = () => {
  const { t } = useTranslation();
  // Cancellation banner dismiss state (non-persistent — reappears on refresh)
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { namespaceId = '', stringGroupId = '' } = useParams<{
    namespaceId: string;
    stringGroupId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Metadata handed over by the Create String Group modal via navigation
  // state. Captured once on first render — the history entry's state is
  // cleared below, so a refresh or back/forward visit re-fetches real server
  // data instead of replaying stale form data forever.
  const [newGroupMetadata, setNewGroupMetadata] =
    useState<NewStringGroupMetadata | null>(() => {
      const candidate = readNewStringGroupState(location.state);
      // Guard against metadata left on a history entry for a different group.
      return candidate &&
        candidate.stringGroupId === stringGroupId &&
        candidate.namespaceId === namespaceId
        ? candidate
        : null;
    });

  useEffect(() => {
    if (readNewStringGroupState(location.state)) {
      // Same pathname, state stripped: consumes the metadata from the history
      // entry without remounting the page (the copy captured above lives on
      // in component state for this mount).
      navigate(location.pathname, { replace: true, state: null });
    }
    // Mount-only: the metadata is consumed exactly once per page visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: group,
    loading: groupLoading,
    error: groupError,
    apolloError,
    isPrefill,
    refetch: refetchGroup,
  } = useStringGroupData(namespaceId, stringGroupId, newGroupMetadata);

  // Source strings now load separately from metadata: describeStringGroup is a DDB-only metadata
  // read, and the strings table is served by ListStrings (TRANSCOM, all pages). Skipped while the
  // page shows prefilled create-modal metadata (no server-side group exists yet). Driven off the
  // group hook's `isPrefill` (not the static `newGroupMetadata`) so that when the prefill is
  // dismissed and the metadata query switches to server data, this query un-skips and loads too —
  // otherwise the table would stay permanently empty for a just-created group.
  const {
    strings,
    loading: stringsLoading,
    error: stringsError,
    refetch: refetchStrings,
  } = useStringsData(namespaceId, stringGroupId, { skip: isPrefill });

  // Called up here, alongside the other data hooks, because the page returns early
  // for the loading and error states below — a hook next to the `canEdit` line it
  // feeds would be a conditional hook call.
  const permissions = useNamespacePermissions(namespaceId);

  // Active change-control (peak-freeze) window, if any. Same query the app-shell
  // banner reads, served from the Apollo cache, so the submit modal can explain
  // the freeze and collect an MCM override, and so the edit modal can say up front
  // that editing a live string will need one. Fails open (null) on error: both
  // then behave as on a normal day and the backend remains the gate.
  const changeControlWindow = useChangeControlStatus();

  const {
    draftState,
    hasPendingChanges,
    addString,
    editString,
    deleteString,
    editGroup,
    markSubmitted,
    clearDraft,
  } = useDraftState(namespaceId, stringGroupId, group?.version ?? 0);

  const { submit, loading: submitLoading } = useSubmitTranslation(
    namespaceId,
    stringGroupId,
    draftState,
    group?.cxIds ?? [],
    // Frozen group properties the submit contract requires. useStringGroupData
    // already applies the `en-US` fallback for a group whose locale is absent, so
    // the '' default here only covers the pre-load window where `group` is null
    // and no submit can be issued anyway.
    group?.sourceLocale ?? '',
    group?.cxFamilyId ?? '',
    // Genuinely-new group: the page is still on create-modal prefill metadata, so
    // its `version: 1` is a local placeholder and no version exists server-side.
    // Makes the submit omit `baseVersion` so the group's first version is numbered
    // 1 (the backend would otherwise derive baseVersion + 1 = 2). Flips false as
    // soon as the prefill is dismissed, from which point every submit carries the
    // real server version — an absent baseVersion on a group that has versions is
    // a 409.
    isPrefill,
    // The Create String Group modal's teamName/description, carried here on the
    // same router-state handoff that drives the prefill. They ride on the group's
    // FIRST submit as `groupMetadata` — the only submission the backend accepts
    // them on — and the hook gates them on `isPrefill` above, so they can never
    // leak onto a later submit (which would be a 400).
    newGroupMetadata,
  );

  // Modal visibility state
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editGroupModalVisible, setEditGroupModalVisible] = useState(false);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  // True from a successful cancellation request until the group's status
  // actually leaves "in progress". TRANSCOM processes the cancellation
  // asynchronously, so `translationStatus` lingers on IN_PROGRESS for a while
  // after the mutation returns; without this flag the action-bar's Cancel button
  // (gated only on `isInProgress`) re-enables in that window and the user can
  // re-issue a cancellation already in flight. Session-only (not persisted):
  // there is no server-side "cancellation requested" signal to resume from, and
  // a reload simply falls back to the live in-progress status. Cleared by the
  // effect below once the status transitions to a terminal state.
  const [cancellationPending, setCancellationPending] = useState(false);
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  const [clearDraftModalVisible, setClearDraftModalVisible] = useState(false);
  // Whether closing (not confirming) the submit-for-translation dialog should
  // discard the draft. True only when the dialog was opened by saving a string
  // group edit — closing it then abandons that just-made edit, so it must not
  // linger. The action-bar Submit path leaves it false so cancelling keeps the
  // user's existing pending changes.
  const [clearDraftOnSubmitDismiss, setClearDraftOnSubmitDismiss] =
    useState(false);
  // True while a successful submission is being finalized: the mutation has
  // resolved but we're still refetching the newly-published version before
  // clearing the draft. Keeps the Submit button disabled/loading across that
  // window so the group can't be double-submitted between the two steps.
  const [finalizingSubmit, setFinalizingSubmit] = useState(false);
  // Version of a submission that was accepted but whose new server version had
  // not yet become queryable when the finalize poll gave up (slow TIS ingest).
  // While set, the draft is deliberately RETAINED — clearing it would blank the
  // user's just-submitted rows during the gap where the server still cannot
  // return them, which reads as "my changes were lost". The reconciliation
  // effect below clears the draft once the server catches up to this version.
  //
  // DERIVED from the persisted draft (draftState.submittedVersion) rather than
  // held in local state, so the retention/reconciliation survives a reload or
  // reopen mid-window: on remount the draft — and this version with it — are
  // re-read from localStorage and the reconciliation resumes. Set via
  // markSubmitted (persists) and cleared implicitly when clearDraft removes the
  // whole draft.
  const pendingSubmittedVersion = draftState?.submittedVersion;
  // Whether the reconciliation effect should announce completion when it finally
  // clears a retained draft. Defaults to true so the RELOAD/resume path (a
  // persisted submittedVersion reconciled on a fresh mount, where the handler
  // never ran this session) still gets a "now live" confirmation. The in-session
  // FAST path sets it false because its immediate success flash already covers
  // completion — announcing again would double-flash. The in-session SLOW path
  // leaves it true so the deferred completion is confirmed. A ref (not state)
  // because it only gates a side effect and must not itself trigger a render.
  const announceOnReconcileRef = useRef(true);
  // Acknowledgement modal ("safe to leave") shown the moment a submission is
  // accepted (HTTP 202), before TRANSCOM confirms it. The version/email are
  // captured from the submission so the copy can name them.
  const [ackModalVisible, setAckModalVisible] = useState(false);
  const [ackVersion, setAckVersion] = useState<number | undefined>(undefined);
  const [ackEmail, setAckEmail] = useState<string | undefined>(undefined);
  const [editingString, setEditingString] = useState<StringInfo | null>(null);
  const [editingIsDraftAdded, setEditingIsDraftAdded] = useState(false);
  // A submission the service refused on change-control grounds, shown in a
  // dialog (not the Flashbar) until the operator closes it. Null when none.
  const [changeControlRefusal, setChangeControlRefusal] =
    useState<ChangeControlRefusal | null>(null);

  // Version history panel state
  const [versionHistoryVisible, setVersionHistoryVisible] = useState(false);
  const {
    data: versionHistoryData,
    loading: versionHistoryLoading,
    error: versionHistoryError,
  } = useVersionHistory(versionHistoryVisible, namespaceId, stringGroupId);

  // Step 7: Diff/View/Rollback modal state
  const [diffModalVisible, setDiffModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [rollbackModalVisible, setRollbackModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(0);

  // Flashbar messages
  const [flashItems, setFlashItems] = useState<
    FlashbarProps.MessageDefinition[]
  >([]);

  // Auto-dismiss timer refs — clean up on unmount
  const autoDismissTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    const timers = autoDismissTimers.current;
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  /**
   * Adds a flashbar item. Success messages auto-dismiss after 5 seconds unless
   * `autoDismiss` is false — used for the submit-for-translation confirmation,
   * which must persist so the user isn't left wondering whether it went through.
   */
  const addFlashItem = useCallback(
    (item: FlashbarProps.MessageDefinition, autoDismiss = true) => {
      const id = item.id ?? `flash-${Date.now()}`;
      const newItem: FlashbarProps.MessageDefinition = {
        ...item,
        id,
        // Flashbar items render silently to screen readers unless they carry an
        // ariaRole, so derive one from the type (error/warning → assertive
        // 'alert', otherwise polite 'status') unless the caller set it.
        ariaRole: item.ariaRole ?? flashAriaRole(item.type),
        dismissible: true,
        dismissLabel: t('flash-dismiss'),
        onDismiss: () =>
          setFlashItems((prev) => prev.filter((f) => f.id !== id)),
      };

      setFlashItems((prev) => [...prev, newItem]);

      // Auto-dismiss success messages after 5 seconds
      if (item.type === 'success' && autoDismiss) {
        const timer = setTimeout(() => {
          setFlashItems((prev) => prev.filter((f) => f.id !== id));
        }, FLASHBAR_AUTO_DISMISS_MS);
        autoDismissTimers.current.push(timer);
      }
    },
    [t],
  );

  // Step 8: Navigation guard — warn on unload when there are pending changes
  useEffect(() => {
    if (!hasPendingChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers (older Chrome/Edge) only show the confirmation prompt
      // when returnValue is set, so set it alongside preventDefault().
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasPendingChanges]);

  // TRANSCOM confirms a submission once describeStringGroup stops answering
  // StringGroupNotReadyException. This is the server-derived interim signal:
  // because it lives on the server, the pending screen it drives survives a
  // reload, a new tab and a different device.
  const isStringGroupNotReady =
    apolloError?.graphQLErrors?.some(
      (e) => getAppSyncErrorType(e) === 'StringGroupNotReadyException',
    ) ?? false;

  // A submission is awaiting confirmation while we are finalizing a just-sent
  // submission in this session, the server still reports the group not-ready,
  // OR a draft is being retained after a slow submit whose version has not yet
  // become queryable (pendingSubmittedVersion). The last case matters when the
  // server keeps returning a *stale* version rather than a
  // StringGroupNotReadyException: finalizingSubmit has already been cleared and
  // the not-ready signal never fires, so without this the Submit button and
  // inline editing would re-enable while the retained draft is still pending —
  // allowing a double-submit and letting the reconciliation effect silently
  // discard any edits made during the retention window. Keeping the group
  // blocked for the full window closes both gaps; it clears the moment the
  // reconciliation effect resolves pendingSubmittedVersion.
  const submissionPending =
    finalizingSubmit ||
    isStringGroupNotReady ||
    pendingSubmittedVersion !== undefined;

  // Editing the group chains straight into the submit dialog, whose cancel
  // discards the whole draft (handleEditGroupSave / SubmitTranslationModal).
  // While the draft already holds string-level changes, that cancel would drop
  // them silently — so block the Edit Group entry point until the user submits
  // or discards those pending string additions/edits/deletions first. Group
  // (cxIds) edits themselves are not counted: they are exactly what Edit Group
  // stages, and are the changes the submit dialog is there to apply.
  const hasPendingStringChanges =
    (draftState?.added.length ?? 0) > 0 ||
    (draftState?.edited.length ?? 0) > 0 ||
    (draftState?.deleted.length ?? 0) > 0;

  // Whether the group would submit at least one string. Mirrors the StringsTable
  // merge (server strings minus pending deletions, plus drafted additions not
  // already on the server) so Submit is gated on exactly what the user sees in
  // the table. A group is created in TRANSCOM only on its first submission and
  // an empty submission is rejected, so Submit stays disabled until this is > 0.
  const effectiveStringCount = useMemo(() => {
    const deletedSet = new Set(draftState?.deleted ?? []);
    const serverIds = new Set(strings.map((s) => s.stringId));
    const liveServerCount = strings.filter(
      (s) => !deletedSet.has(s.stringId),
    ).length;
    const newlyAddedCount = (draftState?.added ?? []).filter(
      (a) => !serverIds.has(a.stringId),
    ).length;
    return liveServerCount + newlyAddedCount;
  }, [strings, draftState]);

  // Resolve the pending screen in place: while the server reports not-ready,
  // re-query on an interval until confirmation clears the error — no full
  // reload. While finalizing a same-session submit, waitForPublishedVersion
  // already polls, so defer to it here to avoid double-polling.
  useEffect(() => {
    if (!isStringGroupNotReady || finalizingSubmit) return;
    const id = setInterval(() => {
      /* istanbul ignore next -- fires only in the server-derived not-ready
         reload state (group cached AND describeStringGroup answering
         StringGroupNotReadyException), which requires a cache-and-network
         refetch-error condition not reproducible in a unit test. */
      void refetchGroup();
    }, SUBMISSION_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isStringGroupNotReady, finalizingSubmit, refetchGroup]);

  // Self-resolve a draft retained after a slow submit (pendingSubmittedVersion)
  // when the server keeps returning a *stale* version rather than a
  // StringGroupNotReadyException. On that path the not-ready poll above never
  // runs (isStringGroupNotReady stays false) and finalizingSubmit has already
  // been cleared, so nothing else would ever refetch the group: group.version
  // would never advance, the reconciliation effect below would never fire,
  // pendingSubmittedVersion would never clear, and submissionPending would stay
  // true indefinitely — locking Submit/inline-editing and pinning the retained
  // draft until a manual reload. Poll describeStringGroup until the submitted
  // version becomes queryable; the reconciliation effect then clears the draft
  // and this interval tears down with it. Skipped while the not-ready poll is
  // already running so the two never double-poll.
  useEffect(() => {
    if (pendingSubmittedVersion === undefined || isStringGroupNotReady) return;
    const id = setInterval(() => {
      void refetchGroup();
      // Strings come from a separate ListStrings query (TRANSCOM) that the
      // reconciliation effect also gates on, so refresh it alongside the
      // metadata — otherwise a snapshot that reflected the version but not yet
      // the content would never be re-observed and the draft would never clear.
      void refetchStrings();
    }, SUBMISSION_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [
    pendingSubmittedVersion,
    isStringGroupNotReady,
    refetchGroup,
    refetchStrings,
  ]);

  // When the server-derived interim signal clears (StringGroupNotReadyException
  // stops firing), TRANSCOM has confirmed the submission and a new version is
  // live. The interval above refetches metadata (updating the banner/version),
  // but the strings table is served by a separate ListStrings query — without
  // refreshing it too, the table keeps showing the pre-submission snapshot.
  // Only fires on the server-derived path (reload / new tab / other device);
  // in a same-session submit, handleSubmitConfirm already refetches strings
  // after waitForPublishedVersion, so finalizingSubmit gates this out.
  const wasStringGroupNotReady = useRef(false);
  useEffect(() => {
    if (
      !finalizingSubmit &&
      wasStringGroupNotReady.current &&
      !isStringGroupNotReady
    ) {
      void refetchStrings();
      // Refresh the progress/status too — the strings snapshot alone leaves the
      // Translation Progress panel showing the pre-submission breakdown.
      void refetchGroup();
    }
    wasStringGroupNotReady.current = isStringGroupNotReady;
  }, [isStringGroupNotReady, finalizingSubmit, refetchStrings, refetchGroup]);

  // While TRANSCOM reports the translation IN_PROGRESS, re-query on a fixed
  // interval so the Translation Progress panel and status advance to completion
  // in place — TRANSCOM pushes nothing, so a manual reload is otherwise the only
  // way the page sees a locale finish or the group reach COMPLETE/FAILED. The
  // interval tears down the moment the status leaves IN_PROGRESS (the refetched
  // group flips `translationInProgress` false and this effect re-runs to clear
  // the timer), so it never polls a terminal group. Gated on !submissionPending
  // so it never runs alongside the pre-confirmation polls above (not-ready
  // error / retained draft), which already refetch on their own cadence.
  const translationInProgress =
    group?.translationStatus === TranslationStatus.InProgress;
  useEffect(() => {
    if (!translationInProgress || submissionPending) return;
    const id = setInterval(() => {
      void refetchGroup();
      // The strings table is served by a separate ListStrings query whose
      // per-string translationStatus advances alongside the group's; refresh it
      // together so the table and the progress panel never disagree.
      void refetchStrings();
    }, IN_PROGRESS_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [translationInProgress, submissionPending, refetchGroup, refetchStrings]);

  // Clear the post-cancellation lock once the group leaves IN_PROGRESS. The
  // in-progress poll above keeps refetching until TRANSCOM's cancellation lands
  // and the status flips to CANCELLED (or any other terminal state); at that
  // point `translationInProgress` goes false and the Cancel button no longer
  // needs the flag to stay disabled (it is now gated by the status itself).
  useEffect(() => {
    if (cancellationPending && !translationInProgress) {
      setCancellationPending(false);
    }
  }, [cancellationPending, translationInProgress]);

  const handleCreateClick = useCallback(() => {
    recordRumEvent(RUM_CLICK_EVENT.CreateString);
    setCreateModalVisible(true);
  }, []);

  const handleEditGroupClick = useCallback(() => {
    recordRumEvent(RUM_CLICK_EVENT.EditGroup);
    setEditGroupModalVisible(true);
  }, []);

  const handleRenameConfirm = useCallback(
    (newName: string) => {
      setRenameModalVisible(false);
      if (newName === stringGroupId) return;
      // Move any drafted strings to the new key BEFORE the URL (and therefore
      // the draft key useDraftState reads) changes, so the work the user has
      // already done follows the rename instead of being stranded under the old
      // name.
      renameDraft(namespaceId, stringGroupId, newName);
      // Keep the prefill metadata in lockstep with the URL: the page title and
      // breadcrumb render from it while the group is unsubmitted.
      setNewGroupMetadata((prev) =>
        prev ? { ...prev, stringGroupId: newName } : prev,
      );
      // The component stays mounted across this param change (same route
      // element), so useParams().stringGroupId updates in place and every
      // consumer keyed off it — the draft, the submit mutation, and the
      // post-submit refetch — targets the new name automatically.
      navigate(buildStringGroupPath(namespaceId, newName), {
        replace: true,
        state: null,
      });
    },
    [namespaceId, stringGroupId, navigate],
  );

  const handleEditClick = useCallback(
    (stringInfo: StringInfo) => {
      // If this string has been edited in the draft, show draft values instead of server values
      const draftEdited = draftState?.edited.find(
        (e) => e.stringId === stringInfo.stringId,
      );
      const draftAdded = draftState?.added.find(
        (a) => a.stringId === stringInfo.stringId,
      );
      const draftVersion = draftEdited ?? draftAdded;

      if (draftVersion) {
        setEditingString({
          ...stringInfo,
          sourceText: draftVersion.sourceText,
          contextNote: draftVersion.contextNote,
          inlineMarkupType: draftVersion.inlineMarkupType,
          messageFormatType: draftVersion.messageFormatType,
          variants: draftVersion.variants ?? stringInfo.variants,
        });
      } else {
        setEditingString(stringInfo);
      }
      setEditingIsDraftAdded(!!draftAdded);
      setEditModalVisible(true);
    },
    [draftState],
  );

  const handleCreateSubmit = useCallback(
    (strings: DraftStringInput[]) => {
      for (const s of strings) {
        addString(s);
      }
    },
    [addString],
  );

  const handleCreateSuccess = useCallback(
    (count: number) => {
      addFlashItem({
        type: 'success',
        content: t('sgd-flash-strings-created', { count }),
        id: `create-success-${Date.now()}`,
      });
    },
    [addFlashItem, t],
  );

  const handleEditSave = useCallback(
    (draft: DraftStringInput, originalStringId?: string) => {
      // If the string ID was renamed (draft-added string only), remove the old
      // entry and add the new one instead of an in-place update.
      if (originalStringId && originalStringId !== draft.stringId) {
        deleteString(originalStringId);
        addString(draft);
      } else {
        editString(draft);
      }
      addFlashItem({
        type: 'success',
        // `stringId` is a TRANSCOM identifier, never translated.
        content: t('sgd-flash-string-updated', { stringId: draft.stringId }),
        id: `edit-success-${Date.now()}`,
      });
    },
    [editString, deleteString, addString, addFlashItem, t],
  );

  const handleEditGroupSave = useCallback(
    (edits: GroupEdits) => {
      editGroup(edits);
      addFlashItem({
        type: 'info',
        // Not "updated successfully": the edit is only staged in the draft at
        // this point and is discarded if the submit dialog opened below is
        // cancelled (clearDraftOnSubmitDismiss). Claiming persistence here would
        // be false in that case — so state that the change still needs the
        // submission to be applied.
        content: t('sgd-metadata-staged'),
        id: GROUP_EDIT_STAGED_FLASH_ID,
      });
      setEditGroupModalVisible(false);
      // Submit the translation right after the group update: chain straight into
      // the submit-for-translation dialog. Closing that dialog abandons this
      // just-saved edit, so flag the draft to be cleared on dismiss.
      setClearDraftOnSubmitDismiss(true);
      setSubmitModalVisible(true);
    },
    // `t` is a dependency: without it a locale change leaves the previous message queued.
    [t, editGroup, addFlashItem],
  );

  const handleSubmit = useCallback(() => {
    // Action-bar submit of existing pending changes: cancelling the dialog keeps
    // the draft intact.
    setClearDraftOnSubmitDismiss(false);
    setSubmitModalVisible(true);
  }, []);

  const handleClearDraftConfirm = useCallback(() => {
    clearDraft();
    setClearDraftModalVisible(false);
    addFlashItem({
      type: 'success',
      content: t('sgd-draft-discarded'),
      id: `clear-draft-success-${Date.now()}`,
    });
    // `t` is a dependency for the same reason as above.
  }, [t, clearDraft, addFlashItem]);

  // Clear the transient "change staged" note the moment the submit dialog it
  // chained into closes — by Confirm, Cancel, Esc, overlay or X. Keying on the
  // dialog being closed (rather than clearing it in each close handler) covers
  // every path uniformly. On Confirm this runs before the submit resolves its
  // own success flash, so the two never appear together; on Cancel the staged
  // change is discarded, so the note is equally moot. Returns `prev` unchanged
  // when the note is absent (the common action-bar submit) to avoid a needless
  // re-render.
  useEffect(() => {
    if (submitModalVisible) return;
    setFlashItems((prev) =>
      prev.some((f) => f.id === GROUP_EDIT_STAGED_FLASH_ID)
        ? prev.filter((f) => f.id !== GROUP_EDIT_STAGED_FLASH_ID)
        : prev,
    );
  }, [submitModalVisible]);

  /**
   * Polls describeStringGroup until the newly-published `targetVersion` is
   * queryable. TIS ingests a submission asynchronously, so the first refetch
   * after submit frequently returns the prior version (or a transient
   * not-ready error). Retries on a short backoff and resolves true once the
   * server reports a version >= target, or false if the budget elapses first.
   * When `targetVersion` is undefined (mutation returned no version) a single
   * refetch is performed and treated as success.
   */
  const waitForPublishedVersion = useCallback(
    async (targetVersion: number | undefined): Promise<boolean> => {
      const observed = await refetchGroup();
      if (targetVersion === undefined) return true;
      if (observed !== undefined && observed >= targetVersion) return true;

      // ~12s total across five retries — long enough to cover typical TIS
      // ingestion without pinning the UI in a loading state for minutes.
      const backoffMs = [1000, 2000, 3000, 3000, 3000];
      for (const delay of backoffMs) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        const next = await refetchGroup();
        if (next !== undefined && next >= targetVersion) return true;
      }
      return false;
    },
    [refetchGroup],
  );

  // Whether the draft changes text that is already live — inside a window, that is
  // what makes an MCM required. New strings and new CX variants on existing strings
  // are exempt (design §8.3), so each edited entry is compared against the live
  // strings rather than trusting the `edited` list. A never-submitted group has
  // nothing live to edit.
  const editsLiveStrings = useMemo(
    () => !isPrefill && draftEditsLiveStrings(draftState, strings),
    [isPrefill, draftState, strings],
  );

  const handleSubmitConfirm = useCallback(
    async (notificationEmail: string, mcmId?: string) => {
      setSubmitModalVisible(false);
      const result = await submit(notificationEmail, mcmId);
      if (!result.success) {
        // A change-control refusal needs reading and acting on before any retry,
        // so it gets a dialog of its own instead of a flash item that can be
        // dismissed unread among the success/pending notices.
        if (result.changeControlBlocked) {
          setChangeControlRefusal(result.changeControlBlocked);
          return;
        }
        const message = result.error ?? t('sgd-submit-failed');
        // Resolved through the same helper the alert uses, so both surfaces agree
        // on whether a link exists and where it points.
        const bindleLink = resolveBindleAccessLink(result.deniedNamespace);
        addFlashItem({
          type: 'error',
          /*
           * A refused submission surfaces here rather than in AccessDeniedAlert:
           * the submit modal closes on confirm (optimistic UX), so by the time the
           * answer arrives there is no modal left to render an alert in. The
           * service's sentence still goes out verbatim — only the Bindle link is
           * added beside it, through the component the alert uses, so both
           * surfaces offer the identical href and label.
           */
          content: bindleLink ? (
            <SpaceBetween size="xxs">
              <span>{message}</span>
              <BindleAccessLinks link={bindleLink} />
            </SpaceBetween>
          ) : (
            message
          ),
          id: `submit-error-${Date.now()}`,
        });
        return;
      }

      // Acknowledge and release immediately on acceptance (HTTP 202): the modal
      // tells the user it is safe to leave. From here the server-derived pending
      // banner owns the wait until TRANSCOM confirms.
      setAckEmail(notificationEmail);
      setAckVersion(
        result.version !== undefined ? Number(result.version) : undefined,
      );
      setAckModalVisible(true);

      // Keep the draft (and therefore the user's edited/added rows) on screen
      // while we poll for the newly-published version. Clearing the draft before
      // the fresh server data is queryable would blank the edits for the whole
      // ingestion window — the reported bug. finalizingSubmit keeps the Submit
      // button disabled/loading across the poll so the group can't be
      // double-submitted meanwhile.
      const targetVersion =
        result.version !== undefined ? Number(result.version) : undefined;
      setFinalizingSubmit(true);
      const reflected = await waitForPublishedVersion(targetVersion);
      setFinalizingSubmit(false);

      if (reflected) {
        void refetchStrings();
        if (targetVersion === undefined) {
          // No version to reconcile against (mutation returned none) — clear
          // best-effort. Rare, and there is no durable signal to defer to.
          clearDraft();
        } else {
          // Do NOT clear here even though the metadata version is queryable:
          // the strings table is fed by ListStrings (TRANSCOM), which can lag
          // the version, so clearing now would blank the user's rows the instant
          // they close the acknowledgement modal. Retain the draft (keeping the
          // merged rows on screen) and let the reconciliation effect clear it
          // once the STRINGS actually reflect the submission. The fast path
          // already shows the success flash below, so reconcile silently.
          announceOnReconcileRef.current = false;
          markSubmitted(targetVersion);
        }
        addFlashItem(
          {
            type: 'success',
            content:
              result.version !== undefined
                ? t('sgd-flash-submit-success-version', {
                    version: result.version,
                  })
                : t('sgd-flash-submit-success'),
            id: `submit-success-${Date.now()}`,
          },
          // Keep this confirmation on screen until the user dismisses it — the
          // submit flow is significant enough that a 5s auto-dismiss risks the
          // user missing it.
          false,
        );
      } else {
        // Submission succeeded but TIS has not finished ingesting the new
        // version yet, so it is not queryable. Do NOT clear the draft: doing so
        // would blank the user's just-submitted rows until ingestion completes,
        // which reads as data loss. Retain it and hand the version to the
        // reconciliation effect, which clears it once the server (metadata AND
        // strings) catches up. This is the slow path, so re-announce completion
        // when it lands. `reflected` is only false when a concrete target
        // version was polled for — an undefined version short-circuits to
        // success in waitForPublishedVersion — so result.version is defined.
        announceOnReconcileRef.current = true;
        markSubmitted(targetVersion as number);
        addFlashItem({
          type: 'info',
          content: t('sgd-flash-submit-processing', {
            version: result.version,
          }),
          id: `submit-processing-${Date.now()}`,
        });
      }
    },
    [
      t,
      submit,
      clearDraft,
      markSubmitted,
      addFlashItem,
      waitForPublishedVersion,
      refetchStrings,
    ],
  );

  // Reconciles a draft retained after submit (see pendingSubmittedVersion). Two
  // conditions must BOTH hold before clearing, so the user never sees a gap:
  //   1. describeStringGroup's version has reached the submitted version, and
  //   2. the ListStrings snapshot actually reflects the draft (added rows
  //      present, deleted rows gone) — the version alone leads TRANSCOM.
  // Until both hold the draft stays, and the table's draft/server merge keeps
  // the user's rows visible; once they hold, clearing swaps to identical server
  // rows seamlessly. Fresh data arrives via the background polls above, the
  // handler's own refetch, or a revisit. clearDraft removes the draft (and its
  // submittedVersion), so pendingSubmittedVersion becomes undefined next render
  // and this effect no-ops thereafter.
  useEffect(() => {
    if (pendingSubmittedVersion === undefined) return;
    if ((group?.version ?? 0) < pendingSubmittedVersion) return;
    if (!serverStringsReflectDraft(strings, draftState)) return;
    clearDraft();
    // Guarantee the table pulls the published content once the draft is
    // dropped. The gate above only structurally verifies added/deleted IDs;
    // edited strings are not text-compared (see serverStringsReflectDraft), so
    // an edit-only draft clears as soon as the version matches, possibly while
    // the observed strings snapshot still holds the pre-edit text. Refetching
    // here swaps that stale text for the published content instead of leaving
    // it until a manual reload.
    void refetchStrings();
    // Also refresh the group metadata so the Translation Progress panel and
    // status reflect the just-published submission (now "in progress"). The
    // progress bar is fed by group.translationProgress, which TRANSCOM populates
    // asynchronously; without this refetch it would keep showing the pre-submit
    // breakdown until the user manually reloaded the page (the reported bug).
    void refetchGroup();
    if (announceOnReconcileRef.current) {
      announceOnReconcileRef.current = false;
      addFlashItem({
        type: 'success',
        content: t('sgd-flash-submit-live', {
          version: pendingSubmittedVersion,
        }),
        id: `submit-reflected-${Date.now()}`,
      });
    }
  }, [
    t,
    pendingSubmittedVersion,
    group?.version,
    strings,
    draftState,
    clearDraft,
    refetchStrings,
    refetchGroup,
    addFlashItem,
  ]);

  const handleCancelTranslation = useCallback(() => {
    setCancelModalVisible(true);
  }, []);

  const handleCancelSuccess = useCallback(() => {
    setCancelModalVisible(false);
    // Keep the action-bar Cancel button locked until the status actually leaves
    // IN_PROGRESS. The cancellation is processed asynchronously by TRANSCOM, so
    // the refetch below will still report IN_PROGRESS for a while; without this
    // the button would re-enable immediately and invite a duplicate request.
    setCancellationPending(true);
    // Refetch so translationStatus/translationProgress (and the banner and
    // action-bar states derived from them) reflect the cancellation.
    refetchGroup();
    refetchStrings();
    addFlashItem({
      type: 'success',
      content: t('sgd-translation-cancelled-flash'),
      id: `cancel-success-${Date.now()}`,
    });
    // `t` is a dependency for the same reason as above.
  }, [t, addFlashItem, refetchGroup, refetchStrings]);

  const handleVersionHistoryToggle = useCallback(() => {
    // Emit only on the closed -> open transition. Do this OUTSIDE the state updater: React may
    // invoke an updater more than once for a single update (e.g. twice under StrictMode in dev),
    // which would double-emit the event. Reading the current value keeps the updater pure.
    if (!versionHistoryVisible) {
      recordRumEvent(RUM_CLICK_EVENT.VersionHistory);
    }
    setVersionHistoryVisible((prev) => !prev);
  }, [versionHistoryVisible]);

  // Step 7: Version history action handlers
  const handleVersionDiff = useCallback((version: number) => {
    setSelectedVersion(version);
    setDiffModalVisible(true);
  }, []);

  const handleVersionView = useCallback((version: number) => {
    setSelectedVersion(version);
    setViewModalVisible(true);
  }, []);

  const handleVersionRollback = useCallback((version: number) => {
    recordRumEvent(RUM_CLICK_EVENT.Rollback);
    setSelectedVersion(version);
    setRollbackModalVisible(true);
  }, []);

  const handleRollbackSuccess = useCallback(
    (newVersion: number) => {
      setRollbackModalVisible(false);
      refetchGroup();
      refetchStrings();
      // Re-open version history panel to refetch updated history
      if (versionHistoryVisible) {
        setVersionHistoryVisible(false);
        // Toggle back on next tick so the lazy query re-fires
        setTimeout(() => setVersionHistoryVisible(true), 0);
      }
      addFlashItem({
        type: 'success',
        content: t('sgd-flash-rollback-submitted', { version: newVersion }),
        id: `rollback-success-${Date.now()}`,
      });
    },
    [addFlashItem, refetchGroup, refetchStrings, versionHistoryVisible, t],
  );

  // Gate the spinner on the initial load only. useStringGroupData is
  // cache-and-network, so `loading` flips true again on every refetch() the
  // submit / cancel / rollback handlers fire — blanking the page would unmount
  // the Flashbar carrying the success message they just queued.
  if (groupLoading && !group) {
    return <Spinner size="large" />;
  }

  // Detect the transient "string group not ready" condition — a 409 from the
  // backend that means TIS is still ingesting the newly submitted version. Show
  // a distinct retryable dialog instead of the generic error panel.
  // `isStringGroupNotReady` is derived once, above, alongside the interim
  // pending state it also drives.
  if (!group && isStringGroupNotReady) {
    // A fresh submission is still being ingested by TIS (backend 409). The
    // publish itself succeeded — the version just isn't queryable yet — so
    // surface a calm info flash (matching the standard "submitted successfully,
    // may take a moment to appear" treatment) rather than an error panel.
    //
    // This is a full-page state (there is no group data to fall back to), so it
    // is not dismissible: the Refresh action is the single recovery path, and a
    // dismiss would strand the user on a blank page.
    return (
      <Flashbar
        items={[
          {
            type: 'info',
            header: t(STRING_GROUP_NOT_READY_HEADER_KEY),
            content: t(STRING_GROUP_NOT_READY_MESSAGE_KEY),
            // Announced politely: this item is built inline (not via
            // addFlashItem), so it sets its own ariaRole.
            ariaRole: 'status',
            action: (
              <Button
                onClick={() => refetchGroup()}
                ariaLabel={t('sgd-refresh-aria')}
              >
                {t('sgd-refresh')}
              </Button>
            ),
            id: 'not-ready-info',
          },
        ]}
      />
    );
  }

  if (!group) {
    /*
     * A refusal keeps the backend's own wording, and `apolloError` is narrowed
     * first: `isAccessDenied` answers true only for a failure that HAS GraphQL
     * errors, so asserting that here keeps the alert from being handed an empty
     * string — a red alert with no text in it. Rendered inside the shell, so the
     * nav and header stay available to navigate somewhere permitted.
     */
    if (apolloError && isAccessDenied(apolloError)) {
      return (
        <AccessDeniedAlert
          message={getAccessDeniedMessage(apolloError)}
          deniedNamespace={getAccessDeniedNamespace(apolloError)}
          data-testid="string-group-access-denied"
        />
      );
    }
    /*
     * Non-dismissible, matching the other pages' load-error treatment: this whole
     * page is the one message, so dismissing it would leave a blank page with no
     * route back short of a browser reload.
     */
    return (
      <Alert
        type="error"
        header={t('sgd-load-failed')}
        data-testid="string-group-load-error"
      >
        {groupError?.message ?? t('sgd-load-failed-body')}
      </Alert>
    );
  }

  // String groups owned by a non-LangHub client (e.g. AST) are surfaced
  // read-only: all edit operations (create/edit strings, edit group, submit,
  // cancel, rollback) are disabled.
  //
  // Two independent conditions, both required. Client ownership says whether this
  // group is editable AT ALL — LangHub cannot write to another client's source.
  // SOURCE_MANAGEMENT says whether THIS CALLER may write to this namespace. Neither
  // implies the other: a LangHub-owned group is still read-only for a viewer without
  // the permission, and the permission does not make an AST-owned group writable.
  const isEditableClient = isLangHubClient(group.clientId);
  const canEdit = isEditableClient && permissions.canManageSource;

  // Collect existing string IDs for uniqueness validation in create modal
  const existingStringIds = strings.map((s) => s.stringId);
  const draftAddedStringIds = (draftState?.added ?? []).map((s) => s.stringId);

  return (
    <SpaceBetween size="l">
      <StackedBreadcrumbGroup
        items={[
          {
            topText: t('namespace-breadcrumb-label'),
            bottomText: group.namespaceId,
            href: buildNamespacePath(group.namespaceId),
          },
          {
            topText: t('breadcrumb-string-group'),
            bottomText: group.stringGroupId,
          },
        ]}
      />

      {flashItems.length > 0 && <Flashbar items={flashItems} />}

      {/* Server-derived interim pending screen: shown while a submission awaits
          TRANSCOM confirmation. Resolves in place via the poll effect above. */}
      {submissionPending && (
        <InterimSubmissionBanner
          notificationEmail={ackEmail}
          onTrackTranslationsClick={() => navigate(TRACK_MY_TRANSLATIONS_PATH)}
        />
      )}

      {group.translationStatus === TranslationStatus.Canceled &&
        !bannerDismissed && (
          <Alert
            type="warning"
            dismissible
            dismissAriaLabel={t('sgd-cancel-notice-dismiss-aria')}
            onDismiss={() => setBannerDismissed(true)}
          >
            {t('sgd-version-cancelled-notice')}
          </Alert>
        )}

      <PageHeader
        title={group.stringGroupId}
        onVersionHistoryClick={handleVersionHistoryToggle}
        onEditGroupClick={handleEditGroupClick}
        canEdit={canEdit}
        isEditableClient={isEditableClient}
        permissionsLoading={permissions.loading}
        submissionPending={submissionPending}
        editGroupLockedByPendingChanges={hasPendingStringChanges}
        groupNeverSubmitted={isPrefill}
        translationInProgress={translationInProgress}
        // A never-submitted group's name is only a local identifier, so it can
        // still be corrected in place — but only by someone who could submit it,
        // and never mid-submission.
        canRename={isPrefill && canEdit && !submissionPending}
        onRenameClick={() => setRenameModalVisible(true)}
        statusBadge={
          // A never-submitted group (isPrefill) is itself unsaved — it exists
          // only in the browser until its first submission — so the badge shows
          // for it even before any string is drafted, matching the loss-warning
          // banner above. It clears when isPrefill flips false on first submit.
          hasPendingChanges || isPrefill ? (
            <Badge color="blue">{t('sgd-unsaved-changes')}</Badge>
          ) : null
        }
      />

      {/*
        Keyed on client ownership, NOT on `canEdit`. The banner's copy explains
        ownership ("owned by AST and is read-only in LangHub"), which says nothing
        true about a caller who simply lacks SOURCE_MANAGEMENT — on a LangHub-owned
        group it would read "owned by LangHub and is read-only in LangHub". A missing
        permission disables the controls and adds no copy, by decision.
      */}
      {!isEditableClient && (
        <Alert type="info">
          {/* One resource with a `client` parameter: the display name is
              TRANSCOM-owned data, and splitting the sentence around it would
              leave a translator unable to reorder the clause. */}
          {t('sgd-read-only-client-notice', {
            client: getClientDisplayName(group.clientId),
          })}
        </Alert>
      )}

      {/*
        A brand-new group exists only in the browser until its first submission
        creates it in TRANSCOM (isPrefill). Warn the user that leaving now
        discards it, so the change is not silently lost on a back-navigation.
      */}
      {isPrefill && (
        <Alert type="warning" header={t(UNSUBMITTED_GROUP_LOSS_HEADER_KEY)}>
          {t(UNSUBMITTED_GROUP_LOSS_MESSAGE_KEY)}
        </Alert>
      )}

      <div className={styles.progressMetadataRow}>
        <div className={styles.progressContainer}>
          <SpaceBetween size="l">
            <DescriptionPanel description={group.description} />
            <TranslationProgressPanel progress={group.translationProgress} />
          </SpaceBetween>
        </div>
        <div className={styles.metadataContainer}>
          <MetadataPanel group={group} />
        </div>
      </div>

      {/*
        Version history and the submit/cancel action bar are hidden entirely for
        non-LangHub groups (isEditableClient === false). Such groups are ingested
        read-only (e.g. TranscomFileUI), so version rollback, submit, cancel, and
        the "group is under translation…" note have no meaning here — showing them
        (even disabled) only confuses. The read-only info Alert above states the
        group is view-only in LangHub.
      */}
      {isEditableClient && (
        <>
          <VersionHistoryPanel
            visible={versionHistoryVisible}
            stringGroupName={group.stringGroupId}
            versions={versionHistoryData}
            loading={versionHistoryLoading}
            error={versionHistoryError}
            canEdit={canEdit}
            permissionsLoading={permissions.loading}
            translationStatus={group.translationStatus}
            submissionPending={submissionPending}
            onClose={handleVersionHistoryToggle}
            onDiff={handleVersionDiff}
            onView={handleVersionView}
            onRollback={handleVersionRollback}
          />

          <SubmitCancelActionBar
            hasPendingChanges={hasPendingChanges}
            hasStrings={effectiveStringCount > 0}
            translationStatus={group.translationStatus}
            translationProgress={group.translationProgress}
            submitLoading={submitLoading || finalizingSubmit}
            canEdit={canEdit}
            isEditableClient={isEditableClient}
            permissionsLoading={permissions.loading}
            submissionPending={submissionPending}
            // A recorded cancelRequestedOn means TRANSCOM already accepted a cancel; keep it locked across reloads.
            cancellationPending={
              cancellationPending || !!group.cancelRequestedOn
            }
            onSubmit={handleSubmit}
            onCancel={handleCancelTranslation}
            onClearDraft={() => setClearDraftModalVisible(true)}
          />
        </>
      )}

      {/* Strings load separately from metadata via ListStrings (useStringsData); they populate
          the table as soon as the snapshot resolves, independent of the metadata query. */}
      <StringsTable
        strings={strings}
        clientId={group.clientId}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        draftState={draftState}
        canEdit={canEdit}
        permissionsLoading={permissions.loading}
        submissionPending={submissionPending}
        loading={stringsLoading && strings.length === 0}
        loadError={stringsError}
        onCreateClick={handleCreateClick}
        onEditClick={handleEditClick}
      />

      {/* Modals */}
      <CreateStringModal
        visible={createModalVisible}
        sourceLocale={group.sourceLocale}
        existingStringIds={existingStringIds}
        draftAddedStringIds={draftAddedStringIds}
        draftDeletedStringIds={draftState?.deleted ?? []}
        onSubmit={handleCreateSubmit}
        onDismiss={() => setCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      <ChangeControlBlockedModal
        refusal={changeControlRefusal}
        onDismiss={() => setChangeControlRefusal(null)}
      />

      <EditStringModal
        visible={editModalVisible}
        stringInfo={editingString}
        sourceLocale={group.sourceLocale}
        isDraftAdded={editingIsDraftAdded}
        changeControlWindow={changeControlWindow}
        existingStringIds={existingStringIds}
        draftAddedStringIds={draftAddedStringIds}
        onSave={handleEditSave}
        onDismiss={() => {
          setEditModalVisible(false);
          setEditingString(null);
          setEditingIsDraftAdded(false);
        }}
      />

      <EditGroupModal
        visible={editGroupModalVisible}
        group={group}
        draftEdits={draftState?.groupEdits}
        onSave={handleEditGroupSave}
        onDismiss={() => setEditGroupModalVisible(false)}
      />

      <RenameStringGroupModal
        visible={renameModalVisible}
        currentName={group.stringGroupId}
        onSubmit={handleRenameConfirm}
        onDismiss={() => setRenameModalVisible(false)}
      />

      <CancelTranslationModal
        visible={cancelModalVisible}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        version={Number(group.version)}
        onDismiss={() => setCancelModalVisible(false)}
        onSuccess={handleCancelSuccess}
      />

      <ClearDraftModal
        visible={clearDraftModalVisible}
        onDismiss={() => setClearDraftModalVisible(false)}
        onConfirm={handleClearDraftConfirm}
      />

      <SubmitTranslationModal
        visible={submitModalVisible}
        loading={submitLoading}
        onDismiss={() => setSubmitModalVisible(false)}
        onConfirm={handleSubmitConfirm}
        clearDraft={clearDraft}
        shouldClearDraft={clearDraftOnSubmitDismiss}
        changeControlWindow={changeControlWindow}
        editsLiveStrings={editsLiveStrings}
      />

      <SubmissionAcknowledgementModal
        visible={ackModalVisible}
        stringGroupId={group.stringGroupId}
        version={ackVersion}
        notificationEmail={ackEmail}
        onDismiss={() => setAckModalVisible(false)}
        onTrackTranslationsClick={() => {
          setAckModalVisible(false);
          navigate(TRACK_MY_TRANSLATIONS_PATH);
        }}
      />

      <DiffModal
        visible={diffModalVisible}
        version={selectedVersion}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        onDismiss={() => setDiffModalVisible(false)}
      />

      <ViewVersionModal
        visible={viewModalVisible}
        version={selectedVersion}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        versionMeta={versionHistoryData?.find(
          (v) => Number(v.version) === selectedVersion,
        )}
        onDismiss={() => setViewModalVisible(false)}
      />

      <RollbackModal
        visible={rollbackModalVisible}
        version={selectedVersion}
        currentVersion={Number(group.version)}
        namespaceId={namespaceId}
        stringGroupId={stringGroupId}
        sourceLocale={group.sourceLocale}
        cxFamilyId={group.cxFamilyId}
        isCurrentVersionCancelled={
          group.translationStatus === TranslationStatus.Canceled
        }
        changeControlWindow={changeControlWindow}
        onDismiss={() => setRollbackModalVisible(false)}
        onSuccess={handleRollbackSuccess}
      />
    </SpaceBetween>
  );
};

export default StringGroupDetailPage;
